import { useEffect, useState } from "react";
import { hivesApi } from "../../services/hivesApi";
import { useToast } from "../../context/ToastContext";
import { Modal } from "../common/Modal";
import { Button } from "../common/Button";

const STATUSES = ["Active", "Dead", "Sold", "Merged", "Absconded", "Archived"];

/**
 * "Bulk Update" dialog for POST /hives/batch-update. Its request body is
 * { hive_ids, fields } where `fields` is validated against the same
 * HiveUpdateRequest model used for single-hive edits (hive_number, status,
 * box_count, frame_count, qr_code). hive_number and qr_code are per-hive
 * identifiers so applying one value to many hives at once doesn't make
 * sense here -- this form only exposes the fields that are meaningful to
 * set in bulk: status, box_count, frame_count. Only fields the user
 * actually touches are sent (backend ignores nulls, but omitting is cleaner).
 */
export function BulkUpdateDialog({ open, onClose, hiveIds, initialParams, onApplied }) {
  const { notify } = useToast();
  const [form, setForm] = useState({ status: "", box_count: "", frame_count: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      status: initialParams?.status || "",
      box_count: initialParams?.box_count != null ? String(initialParams.box_count) : "",
      frame_count: initialParams?.frame_count != null ? String(initialParams.frame_count) : "",
    });
  }, [open, initialParams]);

  async function submit() {
    const fields = {};
    if (form.status) fields.status = form.status;
    if (form.box_count !== "") fields.box_count = Number(form.box_count);
    if (form.frame_count !== "") fields.frame_count = Number(form.frame_count);
    if (Object.keys(fields).length === 0) return notify("Set at least one field to update", "warning");

    setSubmitting(true);
    try {
      const result = await hivesApi.batchUpdate(hiveIds, fields);
      notify(`Updated ${result.updated} of ${result.requested} hive(s)`, "success");
      onApplied?.();
      onClose();
    } catch (err) {
      notify(err.apiMessage || "Bulk update failed", "danger");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Bulk Update"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting ? "Updating…" : `Update ${hiveIds.length} hive(s)`}
          </Button>
        </>
      }
    >
      <div style={{ display: "grid", gap: "var(--space-3)" }}>
        <p style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
          Only the fields you set below will be changed on {hiveIds.length} selected hive(s). Leave a field blank to
          leave it untouched.
        </p>
        <label>
          Status
          <select id="bulk-update-dialog-status" name="status"
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
            className="touch-target"
            style={{ display: "block", width: "100%" }}
          >
            <option value="">Don't change</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label>
          Box count
          <input id="bulk-update-dialog-box-count" name="box-count"
            type="number"
            min={0}
            max={20}
            value={form.box_count}
            onChange={(e) => setForm((f) => ({ ...f, box_count: e.target.value }))}
            className="touch-target"
            style={{ display: "block", width: "100%" }}
            placeholder="Don't change"
          />
        </label>
        <label>
          Frame count
          <input id="bulk-update-dialog-frame-count" name="frame-count"
            type="number"
            min={0}
            max={200}
            value={form.frame_count}
            onChange={(e) => setForm((f) => ({ ...f, frame_count: e.target.value }))}
            className="touch-target"
            style={{ display: "block", width: "100%" }}
            placeholder="Don't change"
          />
        </label>
      </div>
    </Modal>
  );
}
